import React, { useRef } from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';

import {
  Container, useWindowDimensions,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  Paragraph,
  Taxonomy,
  View,
} from '@uncinc/react-drupal-core';

import {
  Share,
  WysiwygField,
  CampaignBlock,
  Hero,
} from '../../../components';

import NodeMetadata from '../../../components/NodeMetadata';
import ExpertsSlider from '../../../components/ExpertsSlider';
import MemberWall from '../../../components/MemberWall';
import NewsletterBlock from '../../../components/NewsletterBlock';
import Tags from '../../../components/Tags';
import useLoggedInStatus from '../../../hooks/useLoggedInstatus';


import './style.scss';

const NodeInterventionPage = ({ entity, className }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { isLoggedIn } = useLoggedInStatus();
  const classes = classNames(className, 'NodeIntervention');
  const theme = entity.getIn(['field_theme', 0, 'entity']);

  const created = new Date(Date.parse(entity.getIn(['created', 0, 'value'])));
  const title = entity.getIn(['title', 0, 'value'], '');
  const subtitle = entity.getIn(['field_subtitle', 0, 'value'], '');
  const goal = entity.getIn(['field_goal', 0, 'processed']);
  const targetAudience = entity.getIn(['field_target_audience', 0, 'processed']);
  const approach = entity.getIn(['field_approach', 0, 'processed']);
  const body = entity.getIn(['body', 0, 'processed']);
  const type = entity.getIn(['field_intervention_type', 0, 'entity', 'name', 0, 'value'], 'Interventie');
  const experts = entity.getIn(['field_experts'], Immutable.Map());
  const paragraphs = entity.getIn(['field_paragraphs'], Immutable.Map());
  const heroImage = entity.getIn(['field_media_image', 0], Immutable.Map());
  const getStartedTitle = entity.getIn(['field_get_started_title', 0, 'value']);
  const getStartedSubtitle = entity.getIn(['field_get_started_subtitle', 0, 'value']);
  const getStartedParagraphs = entity.getIn(['field_get_started_paragraphs'], Immutable.Map());
  const campaign = entity.getIn(['field_campaign', 0, 'entity'], null);
  const isMemberOnlyContent = entity.getIn(['field_member_only_content', 0, 'value'], false);
  const relatedDossiersTitle = entity.getIn(['field_related_dossiers_title', 0, 'value'], 'Dit artikel is onderdeel van');

  const showMemberWall = !isLoggedIn && isMemberOnlyContent;
  const getStartedRef = useRef();

  let scrollOffset = '-120px';
  if (width < 1200) scrollOffset = '-80px';
  if (width < 768) scrollOffset = '-48px';

  const subjects = entity.getIn(['field_subject']);
  const workfields = entity.getIn(['field_workfield']);
  const relatedDossierArgs = entity.getIn(['field_subject'])
    .concat(entity.getIn(['field_workfield']))
    .concat(entity.getIn(['field_phase_of_life']))
    .concat(entity.getIn(['field_locations']));
  const expertsTitle = entity.getIn(['field_experts_title', 0, 'value'], t('expert.node_title'));

  return (
    <article typeof="schema:WebPage" className={classes}>
      <Hero
        image={heroImage}
      />

      <Container>
        <div className="NodeIntervention__content__wrapper row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="NodeIntervention__content" id="main">
              <NodeMetadata
                icon="icon-copy"
                displayType={type}
                created={created}
                theme={theme}
              />
              <h1>{title}</h1>

              {subtitle && (
                <div className="NodeIntervention__content__subtitle">
                  {subtitle}
                </div>
              )}

              {body && (
                <WysiwygField
                  property="schema:text"
                  className="NodeArticleDetail__body subheading2"
                  html={body}
                />
              )}

              <div className="NodeIntervention__content__steps">
                <div className="container">
                  <div className="row">
                    <div className="col-12">
                      <h2 className="asH4">Doel</h2>
                      {goal && (
                        <WysiwygField
                          property="schema:text"
                          className="NodeArticleDetail__body subheading2"
                          html={goal}
                        />
                      )}
                    </div>
                    <div className="col-12">
                      <h2 className="asH4">Doelgroep</h2>
                      {targetAudience && (
                        <WysiwygField
                          property="schema:text"
                          className="NodeArticleDetail__body subheading2"
                          html={targetAudience}
                        />
                      )}
                    </div>
                    <div className="col-12">
                      <h2 className="asH4">Aanpak</h2>
                      {approach && (
                        <WysiwygField
                          property="schema:text"
                          className="NodeArticleDetail__body subheading2"
                          html={approach}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
      <div className="NodeContentWrapper">

        {paragraphs.size > 0 && (
          <div className="Paragraphs">
            {
              paragraphs.map((item, index) => {
                if (showMemberWall && index > 0) return;
                const paragraphId = item.get('target_id');
                const paragraph = item.getIn(['entity']);

                return (
                  <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                );
              })
            }
          </div>
        )}

        {campaign && (
          <CampaignBlock entity={campaign} />
        )}

        <section className="NodeIntervention__getstarted">
          <div style={{ position: 'absolute', top: scrollOffset, left: 0 }} ref={getStartedRef} />
          <Container>
            <div className="row">
              <div className="col-12">
                <h2>{getStartedTitle}</h2>
                {getStartedSubtitle}

                <div className="NodeIntervention__getstarted__paragraphs">
                  {getStartedParagraphs.size > 0 && (
                    <div className="Paragraphs">
                      {
                        getStartedParagraphs.map((item, index) => {
                          if (showMemberWall && index > 0) return;
                          const paragraphId = item.get('target_id');
                          const paragraph = item.getIn(['entity']);

                          return (
                            <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                          );
                        })
                      }
                    </div>
                  )}

                </div>
              </div>
            </div>
          </Container>
        </section>

        <ExpertsSlider
          expertsTitle={expertsTitle}
          experts={experts}
        />

        {relatedDossierArgs.size > 0 && (
          <div className="TaxonomySubject__related_dossiers">
            <Container>
              <h2>{relatedDossiersTitle}</h2>
              <View id="article_dossiers_related" display="block_1" args={relatedDossierArgs} />
            </Container>
          </div>
        )}

        {entity.getIn(['field_paragraph_feedback', 0]) && (
          <div className="NodeIntervention__feedback">
            {
              entity.getIn(['field_paragraph_feedback']).map((item, index) => {
                const paragraphId = item.get('target_id');
                const paragraph = item.getIn(['entity']);

                return (
                  <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                );
              })
            }
          </div>
        )}

        <Tags subjects={subjects} workfields={workfields} />
        {theme && (
          <Container>
            <div className="row">
              <div className="col-12">
                <Taxonomy
                  id={theme.getIn(['tid', 0, 'value'])}
                  entity={theme}
                  viewMode="card"
                />
              </div>
            </div>
          </Container>
        )}
        <Container>
          <div className="row">
            <div className="col-12 col-lg-8 offset-lg-2">
              <Share pageTitle={entity.getIn(['title', 0, 'value'], '')} />
            </div>
          </div>
        </Container>
      </div>


      {/* Member wall. */}
      {showMemberWall && (
        <MemberWall />
      )}

      {/* Newsletter subscription block */}
      <NewsletterBlock />
    </article>
  );
};


export default NodeInterventionPage;
